import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { postApiCall, getApiCall } from "../../utils/Apicalls";
import InterviewForm from "../../components/InterviewForm";
import SpeechToText from "../../components/SpeechToText";
import Header from "../../components/common/header/Header";
import Footer from "../../components/common/footer/Footer";
import "./admin-home.css";

function AdminHome() {
  const [showSpeechToText, setShowSpeechToText] = useState(false);
  const [sessionConfig, setSessionConfig] = useState(null);
  const [userRemainingTime, setUserRemainingTime] = useState(0);
  const [sessionTimerRef, setSessionTimerRef] = useState(null);
  // eslint-disable-next-line
  const [isStartingSession, setIsStartingSession] = useState(false);
  const navigate = useNavigate();

  // Define endSession first to avoid circular dependency
  const endSession = useCallback(async (isTimeExpired = false) => {
    // Clear the session timer
    if (sessionTimerRef) {
      clearInterval(sessionTimerRef);
      setSessionTimerRef(null);
    }

    // Complete the interview session if there's a session ID
    try {
      const sessionId = localStorage.getItem('currentInterviewSessionId');
      
      if (sessionId) {
        // Calculate token usage based on session duration
        let tokenCount = 0;
        
        if (sessionConfig) {
          // If we have detailed usage stats from the SpeechToText component, use those
          if (sessionConfig.tokenUsage) {
            tokenCount = sessionConfig.tokenUsage;
          } else {
            // Calculate tokens based on session duration
            // For time-expired sessions or when remaining time is 0, use the full allocated time
            const initialTime = sessionConfig.initialDuration || 5; // Default to 5 minutes if not specified
            
            // If remaining time is 0, it means the full session was used
            if (userRemainingTime <= 0 || isTimeExpired) {
              // Calculate tokens based on the full session duration (50 tokens per minute)
              tokenCount = Math.round(initialTime * 50);
              console.log(`Session completed with full duration of ${initialTime} minutes, token count: ${tokenCount}`);
            } else {
              // Calculate tokens based on time used
              const timeUsedInMinutes = (initialTime * 60 - userRemainingTime) / 60;
              tokenCount = Math.round(timeUsedInMinutes * 50);
              console.log(`Session used ${timeUsedInMinutes.toFixed(2)} minutes, token count: ${tokenCount}`);
            }
          }
        } else {
          // If no session config is available, estimate based on a 5-minute session
          tokenCount = 250; // 5 minutes * 50 tokens per minute
          console.log('No session config available, using default token count:', tokenCount);
        }
        
        // Ensure we have at least a minimum token count
        tokenCount = Math.max(tokenCount, 20);
        
        console.log('Completing interview session with token count:', tokenCount);
        
        // Call the complete interview API with the calculated token count
        const result = await postApiCall(`interview-app/interview/complete/${sessionId}`, {
          totalToken: tokenCount
        });
        
        if (result && result.status) {
          console.log('Interview session completed successfully');
          // Remove the session ID from localStorage
          localStorage.removeItem('interView');
          localStorage.removeItem('currentInterviewSessionId');
          
          // If time expired, show a message
          // if (isTimeExpired) {
          //   alert('Your session time has expired.');
          // }
        } else {
          console.error('Failed to complete interview session:', result);
        }
      }
    } catch (error) {
      console.error('Error completing interview session:', error);
    }
    
    // Go back to the form
    setShowSpeechToText(false);
  }, [sessionConfig, userRemainingTime, sessionTimerRef]);

  // Now define startSessionTimer with endSession as a dependency
  // eslint-disable-next-line
  const startSessionTimer = useCallback((initialTime) => {
    // Clear any existing timer
    if (sessionTimerRef) {
      clearInterval(sessionTimerRef);
    }

    // Convert minutes to milliseconds
    const sessionDuration = initialTime * 60 * 1000;
    const endTime = Date.now() + sessionDuration;

    // Start a new timer
    const timerId = setInterval(() => {
      const remaining = Math.max(0, endTime - Date.now());
      const remainingMinutes = remaining / (60 * 1000);

      // Update remaining time in state
      setUserRemainingTime(remainingMinutes);

      // If time is up, end the session
      if (remaining <= 0) {
        clearInterval(timerId);
        endSession(true); // Pass true to indicate time expiration
      }
    }, 1000);

    setSessionTimerRef(timerId);

    // Return cleanup function
    return () => clearInterval(timerId);
  }, [endSession, sessionTimerRef]);

  // Start interview session
  const startInterview = async (config) => {
    try {
      setIsStartingSession(true);
      
      // First, check if user has any remaining time
      const timeResult = await getApiCall('interview-app/interview/details');
      
      if (timeResult && timeResult.result) {
        const remainingMinutes = timeResult.result.remainingMinutes || 0;
        
        // If no remaining time, redirect to payment page
        if (remainingMinutes <= 0) {
          setIsStartingSession(false);
          
          // Show payment confirmation dialog
          if (window.confirm("You have no remaining interview time. Would you like to purchase more time?")) {
            navigate('/checkout');
          }
          return;
        }
        
        // Convert minutes to seconds
        const remainingSeconds = remainingMinutes * 60;
        setUserRemainingTime(remainingSeconds);
        
        // Start a new interview session
        if (!config.sessionId) {
          // Prepare request data
          const requestData = {
            interviewType: config.interviewType || 'general',
            level: config.level || 'beginner',
            topic: config.topic || 'general',
            duration: config.duration || 15
          };
          
          // Store the initial duration for token calculation later
          config.initialDuration = requestData.duration;
          
          console.log('Starting interview session with config:', requestData);
          
          // Call the start interview API
          const result = await postApiCall('interview-app/interview/start', requestData);
          
          if (result && result.status && result.result) {
            // Store the session ID in localStorage for later use
            localStorage.setItem('currentInterviewSessionId', result.result.inserted);
            
            // Update config with session ID
            config.sessionId = result.result.inserted;
            
            // Use remainingMinutes from API response if available
            if (result.result.remainingMinutes !== undefined) {
              // Convert minutes to seconds for the timer (multiply by 60)
              const apiRemainingMinutes = result.result.remainingMinutes;
              const apiRemainingSeconds = apiRemainingMinutes * 60;
              config.sessionTimeRemaining = apiRemainingSeconds;
              setUserRemainingTime(apiRemainingSeconds);
              console.log('Setting remaining time from API:', apiRemainingMinutes, 'minutes =', apiRemainingSeconds, 'seconds');
            } else {
              // Use the previously fetched remaining time
              config.sessionTimeRemaining = remainingSeconds;
              console.log('Using previously fetched remaining time:', remainingMinutes, 'minutes =', remainingSeconds, 'seconds');
            }
          } else {
            console.error('Failed to start interview session:', result);
            alert('Failed to start interview session. Please try again.');
            setIsStartingSession(false);
            return;
          }
        }
        
        // Show the SpeechToText component with the remaining time from API
        setSessionConfig(config);
        // if (config.type == "desktopApp") {
          // navigate('/automatic-diarization');
        // } else {
          setShowSpeechToText(true);
        // }
      } else {
        console.error('Failed to fetch remaining time');
        alert('Failed to check remaining time. Please try again.');
      }
    } catch (error) {
      console.error('Error starting interview session:', error);
      alert('An error occurred while starting the interview session. Please try again.');
    } finally {
      setIsStartingSession(false);
    }
  };

  // Check if user is logged in and get remaining time
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    // Fetch user's remaining time from API
    const fetchRemainingTime = async () => {
      try {
        const result = await getApiCall('interview-app/interview/details');
        if (result && result.result) {
          // Convert minutes to seconds for internal timer (multiply by 60)
          const remainingMinutes = result.result.remainingMinutes || 0;
          const remainingSeconds = remainingMinutes * 60;
          setUserRemainingTime(remainingSeconds);
          console.log('Fetched remaining time from API:', remainingMinutes, 'minutes =', remainingSeconds, 'seconds');
        }
      } catch (error) {
        console.error('Error fetching remaining time:', error);
      }
    };

    fetchRemainingTime();
  }, [navigate]);

  // Clean up on component unmount
  useEffect(() => {
    return () => {
      if (sessionTimerRef) {
        clearInterval(sessionTimerRef);
      }
    };
  }, [sessionTimerRef]);

  // Add an event listener for beforeunload to catch browser/tab closures
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Only handle if we're in an active session
      if (showSpeechToText && localStorage.getItem('currentInterviewSessionId')) {
        // Complete the session before unloading
        endSession(false);
        
        // Modern browsers require returnValue to be set
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [showSpeechToText, endSession]);

  return (
    <>
      <Header />
      <div className="admin-home-container">
        {showSpeechToText ? (
          <SpeechToText 
            onEndSession={endSession} 
            sessionConfig={sessionConfig}
            remainingTime={userRemainingTime} // This should be in seconds
          />
        ) : (
          <InterviewForm 
            onStartSession={startInterview} 
            initialRemainingTime={userRemainingTime}
          />
        )}
      </div>
      <Footer />
    </>
  );
}

export default AdminHome;
